// =========================================================================
// TourBar — the guided tour's bottom bar.
//
// Port of the demo's `#tourbar` (demo line 1470, painted by `paintTour()` at
// line 3866): the stop counter, the stop's title and commentary, and Back /
// Next / End tour. Each step navigates the real pages, so the presenter can
// talk over the bar without losing their place.
//
// Differences from the demo, all additive:
// - Keyboard: ← and → step while focus is inside the bar, and Esc ends the
//   tour. The bar takes focus when a tour starts, so a keyboard user can step
//   straight away.
// - On the last stop Next becomes Finish, rather than a disabled button that
//   drops focus onto <body>.
// - A stop with an `anchor` scrolls that card into view once the page has
//   rendered, with a short ring (none under prefers-reduced-motion).
// - A stop with an `ask` offers the question as a button that sends it to the
//   assistant.
//
// State lives in `shared/tour-engine.ts`. Mounted once, next to ToastHost in
// app/layout.tsx, and renders nothing while no tour is running.
//
// Fork-owned (zone C). UI layer (layer 4).
// =========================================================================

import { useEffect, useRef, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Sparkles, X } from 'lucide-react';
import { cn } from '@/shared/cn';
import { BUTTON_GHOST, BUTTON_PRIMARY } from '@/shared/ui-class';
import { askOem } from '../shared/assistant/ask';
import type { TourStop } from '../shared/seed/tour';
import { tourRoute, useTour } from '../shared/tour-engine';

/** How long to keep looking for a stop's anchor while its scene loads data. */
const ANCHOR_POLL_MS = 100;
const ANCHOR_POLL_TRIES = 25;

const SMALL = 'h-8 px-3 text-[12.5px]';

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function findAnchor(anchor: string): HTMLElement | null {
  // Comment anchors nest (`Asset360Scene.Asset360Scene.IssueSummary`), so a
  // stop names the tail of the path and matches either exactly or as a suffix.
  return document.querySelector<HTMLElement>(
    `[data-comment-anchor="${anchor}"], [data-comment-anchor$=".${anchor}"]`,
  );
}

/**
 * Scroll the active stop's anchor into view once its page is showing. Polls
 * briefly because most scenes render their cards only after their data
 * domain resolves.
 */
function useStopAnchor(stop: TourStop | null, pathname: string) {
  const stopId = stop?.id;
  const anchor = stop?.anchor;
  const stopPath = stop ? tourRoute(stop).split('?')[0] : null;

  useEffect(() => {
    if (!anchor || stopPath !== pathname) return;
    let tries = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = () => {
      const el = findAnchor(anchor);
      if (!el) {
        tries += 1;
        if (tries < ANCHOR_POLL_TRIES) timer = setTimeout(tick, ANCHOR_POLL_MS);
        return;
      }
      const reduced = prefersReducedMotion();
      el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      if (!reduced && typeof el.animate === 'function') {
        el.animate(
          [
            { boxShadow: '0 0 0 3px color-mix(in srgb, var(--accent) 45%, transparent)' },
            { boxShadow: '0 0 0 3px transparent' },
          ],
          { duration: 1800, easing: 'ease-out' },
        );
      }
    };

    timer = setTimeout(tick, ANCHOR_POLL_MS);
    return () => clearTimeout(timer);
  }, [stopId, anchor, stopPath, pathname]);
}

export function TourBar() {
  const tour = useTour();
  const { pathname } = useLocation();
  const barRef = useRef<HTMLElement>(null);
  const wasActive = useRef(tour.active);

  useStopAnchor(tour.active ? tour.stop : null, pathname);

  // Take focus when a tour starts in this session, not when a reload restores
  // one, so a page load never steals focus.
  useEffect(() => {
    if (tour.active && !wasActive.current) barRef.current?.focus({ preventScroll: true });
    wasActive.current = tour.active;
  }, [tour.active]);

  if (!tour.active || typeof document === 'undefined') return null;

  const { stop, index, total, isFirst, isLast } = tour;

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.key === 'ArrowLeft' && !isFirst) {
      event.preventDefault();
      tour.back();
    } else if (event.key === 'ArrowRight' && !isLast) {
      event.preventDefault();
      tour.next();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      tour.end();
    }
  };

  return createPortal(
    <section
      ref={barRef}
      tabIndex={-1}
      role="region"
      aria-label="Guided tour"
      aria-roledescription="tour bar"
      onKeyDown={onKeyDown}
      className="fixed bottom-[18px] left-1/2 z-[70] flex w-[min(940px,calc(100vw-2rem))] -translate-x-1/2 flex-wrap items-center gap-x-4 gap-y-2 rounded-xl px-4 py-2.5 backdrop-blur-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)] sm:flex-nowrap"
      style={{
        background: 'var(--glass-surface-strong)',
        border: '1px solid var(--glass-border-strong)',
        boxShadow: '0 16px 44px -12px var(--shadow-warm-lg)',
      }}
    >
      <span
        className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.08em] tabular-nums"
        style={{ color: 'var(--ink-tertiary)' }}
      >
        Step {index + 1} of {total}
      </span>

      <div className="min-w-0 flex-1" aria-live="polite" aria-atomic="true">
        <b className="block text-[13px] font-semibold" style={{ color: 'var(--ink-primary)' }}>
          {stop.title}
        </b>
        <small
          className="block text-[11.5px] leading-[1.4]"
          style={{ color: 'var(--ink-secondary)' }}
        >
          {stop.body}
        </small>
      </div>

      <div className="ml-auto flex shrink-0 flex-wrap items-center gap-1.5">
        {stop.ask ? (
          <button
            type="button"
            className={cn(BUTTON_GHOST, SMALL)}
            onClick={() => askOem(stop.ask ?? '')}
            title={`Ask the assistant: ${stop.ask}`}
          >
            <Sparkles className="h-3.5 w-3.5" strokeWidth={1.8} aria-hidden="true" />
            Ask it
          </button>
        ) : null}
        <button
          type="button"
          className={cn(BUTTON_GHOST, SMALL)}
          onClick={tour.back}
          disabled={isFirst}
          aria-keyshortcuts="ArrowLeft"
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
          Back
        </button>
        {isLast ? (
          <button type="button" className={cn(BUTTON_PRIMARY, SMALL)} onClick={tour.end}>
            Finish
          </button>
        ) : (
          <button
            type="button"
            className={cn(BUTTON_PRIMARY, SMALL)}
            onClick={tour.next}
            aria-keyshortcuts="ArrowRight"
          >
            Next
            <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          className={cn(BUTTON_GHOST, SMALL)}
          onClick={tour.end}
          aria-keyshortcuts="Escape"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
          End tour
        </button>
      </div>
    </section>,
    document.body,
  );
}
